import React from 'react';
import { Modal, Button, TextField, CircularProgress, FormControlLabel, Checkbox, Autocomplete, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { YardSpot, Zone } from '../../pages/YardSpotterApp/yardDataFetch';
import DestinationFacilityFilter, { DestinationFacilityFilterProps } from 'components/Campus/DestinationFacilityFilter';


interface StagingTrailer {
  puid: string;
  driver_id: string;
  driver_name: string;
  driver_phone_number: string;
  truck_number: string;
  trailer_number: string;
  direction: string;
  carrier_name?: string;
  trailer_uuid?: string;
  current_status?: string;
  shipment_number?: string;
}

interface MoveToYardSpotModalProps {
  open: boolean;
  selectedStagingTrailer: StagingTrailer | null;
  selectedShipmentPuid: string;
  selectedYardSpotForMove: string;
  selectedYardSpotUuid: string;
  selectedZoneFilter: string | null;
  spotFilter: string;
  autoCompleteMoveToYardSpot: boolean;
  assigningYardSpot: boolean;
  assignYardSpotError: string | null;
  validatingShipment: boolean;
  selectedDestinationType: string;
  selectedFacilityZone: string;
  zones: Zone[];
  yardSpots: YardSpot[];
  facilityZones: any[];
  taskBasedLockedSpots: Set<string>;
  lockedSpots: Set<string>;
  mergeLockSets: (set1: Set<string>, set2: Set<string>) => Set<string>;
  onClose: () => void;
  onAssign: () => void;
  onShipmentPuidChange: (value: string) => void;
  onYardSpotForMoveChange: (value: string) => void;
  onYardSpotUuidChange: (value: string) => void;
  onZoneFilterChange: (value: string | null) => void;
  onSpotFilterChange: (value: string) => void;
  onAutoCompleteChange: (checked: boolean) => void;
  onDestinationTypeChange: (value: string) => void;
  onFacilityZoneChange: (value: string) => void;
  onAssignYardSpotErrorChange: (error: string | null) => void;
  destinationFacilityFilter?: DestinationFacilityFilterProps;
}

const MoveToYardSpotModal: React.FC<MoveToYardSpotModalProps> = ({
  open,
  selectedStagingTrailer,
  selectedShipmentPuid,
  selectedYardSpotForMove,
  selectedYardSpotUuid,
  selectedZoneFilter,
  spotFilter,
  autoCompleteMoveToYardSpot,
  assigningYardSpot,
  assignYardSpotError,
  validatingShipment,
  selectedDestinationType,
  selectedFacilityZone,
  zones,
  yardSpots,
  facilityZones,
  taskBasedLockedSpots,
  lockedSpots,
  mergeLockSets,
  onClose,
  onAssign,
  onShipmentPuidChange,
  onYardSpotForMoveChange,
  onYardSpotUuidChange,
  onZoneFilterChange,
  onSpotFilterChange,
  onAutoCompleteChange,
  onDestinationTypeChange,
  onFacilityZoneChange,
  onAssignYardSpotErrorChange,
  destinationFacilityFilter,
}) => {
  if (!selectedStagingTrailer) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={onClose}
          />
          <h1 className="text-xl font-bold mb-4">Move Trailer to Yard Spot</h1>
          
          {/* Trailer details */}
          <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <span className="font-semibold">Trailer:</span> {selectedStagingTrailer.trailer_number}
              </div>
              <div>
                <span className="font-semibold">Driver:</span> {selectedStagingTrailer.driver_name}
              </div>
              <div>
                <span className="font-semibold">Phone:</span> {selectedStagingTrailer.driver_phone_number}
              </div>

              {selectedStagingTrailer.carrier_name && (
                <div className="col-span-2">
                  <span className="font-semibold">SCAC:</span> {selectedStagingTrailer.carrier_name}
                </div>
              )}
            </div>
          </div>
          
          {/* Show error if any */}
          {assignYardSpotError && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {assignYardSpotError}
            </div>
          )}
          
          {/* Shipment Reference Number field - only show if prefilled */}
          {selectedShipmentPuid && (
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Shipment Reference Number</h2>
              <TextField
                label="Enter Reference Number (Optional)"
                variant="outlined"
                fullWidth
                value={selectedShipmentPuid}
                onChange={(e) => onShipmentPuidChange(e.target.value)}
                helperText={selectedShipmentPuid ? "Reference number is read-only when already assigned" : "Enter the shipment reference number if available (e.g. REF12345)"}
                disabled={validatingShipment || assigningYardSpot || !!selectedShipmentPuid}
              />
            </div>
          )}
          
          {/* Auto-complete checkbox */}
          <div className="mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoCompleteMoveToYardSpot}
                  onChange={(e) => onAutoCompleteChange(e.target.checked)}
                  disabled={assigningYardSpot || validatingShipment}
                />
              }
              label="Auto-complete task (task will be marked as completed immediately)"
            />
          </div>
          
          {/* Destination Type Selection */}
          <div className="mb-4">
            <h2 className="font-semibold mb-2">Select Destination Type</h2>
            <ToggleButtonGroup
              value={selectedDestinationType}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  onDestinationTypeChange(value);
                  onYardSpotForMoveChange('');
                  onYardSpotUuidChange('');
                  onFacilityZoneChange('');
                  onAssignYardSpotErrorChange(null);
                }
              }}
              fullWidth
            >
              <ToggleButton value="yard_spot">
                Yard Spot
              </ToggleButton>
              <ToggleButton value="facility_zone">
                Facility Zone
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          {destinationFacilityFilter && (
            <DestinationFacilityFilter
              facilities={destinationFacilityFilter.facilities}
              value={destinationFacilityFilter.value}
              onChange={value => {
                destinationFacilityFilter.onChange(value);
                onYardSpotForMoveChange('');
                onYardSpotUuidChange('');
                onZoneFilterChange(null);
                onSpotFilterChange('');
                onFacilityZoneChange('');
              }}
              disabled={assigningYardSpot || validatingShipment}
            />
          )}

          {selectedDestinationType === 'yard_spot' && (
            <div>
              {/* Zone filter dropdown */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Filter by Zone (Optional)</h2>
                <Autocomplete
                  options={zones.map(zone => ({ 
                    label: zone.name, 
                    value: zone.id,
                    color: zone.color 
                  }))}
                  value={selectedZoneFilter ? zones.find(z => z.id === selectedZoneFilter) ? { 
                    label: zones.find(z => z.id === selectedZoneFilter)!.name, 
                    value: selectedZoneFilter,
                    color: zones.find(z => z.id === selectedZoneFilter)!.color
                  } : null : null}
                  onChange={(_, newValue) => {
                    onZoneFilterChange(newValue?.value || null);
                    // Clear selected yard spot and spot filter when zone filter changes
                    onYardSpotForMoveChange('');
                    onYardSpotUuidChange('');
                    onSpotFilterChange('');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Zone"
                      variant="outlined"
                      fullWidth
                      helperText="Filter yard spots by zone"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded mr-2" 
                          style={{ backgroundColor: option.color }}
                        />
                        {option.label}
                      </div>
                    </li>
                  )}
                />
              </div>

              {/* Available yard spots selection */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Select Yard Spot</h2>
              
              <div className="space-y-3">
                {/* Manual entry */}
                <TextField
                  label="Filter Yard Spots"
                  variant="outlined"
                  fullWidth
                  value={spotFilter}
                  onChange={(e) => {
                    const filterValue = e.target.value;
                    onSpotFilterChange(filterValue);
                    
                    // Auto-selection logic
                    if (filterValue.trim()) {
                      // Get available spots for validation
                      const availableSpots = yardSpots.filter(spot => {
                        const hasValidDisplayNumber = spot.displayNumber && spot.displayNumber.trim();
                        const isAvailable = spot.status === 'available' && !mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id);
                        const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                        return hasValidDisplayNumber && isAvailable && matchesZone;
                      });
                      
                      // Find spots that match the filter
                      const matchingSpots = availableSpots.filter(spot => 
                        spot.displayNumber && 
                        spot.displayNumber.trim() && 
                        spot.displayNumber.toLowerCase().includes(filterValue.toLowerCase())
                      );
                      
                      // Check for exact match
                      const exactMatch = matchingSpots.find(spot => 
                        spot.displayNumber && 
                        spot.displayNumber.trim() && 
                        spot.displayNumber.toLowerCase() === filterValue.toLowerCase()
                      );
                      
                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingSpots.length === 1 && exactMatch && exactMatch.displayNumber.trim()) {
                        onYardSpotForMoveChange(exactMatch.displayNumber);
                        onYardSpotUuidChange(exactMatch.id);
                      } else {
                        // Clear selection if multiple results or no exact match
                        onYardSpotForMoveChange('');
                        onYardSpotUuidChange('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      onYardSpotForMoveChange('');
                      onYardSpotUuidChange('');
                    }
                  }}
                  helperText="Type to filter spots below (e.g. A15)"
                  placeholder="Type spot name to filter..."
                />
                
                {/* Quick selection from available spots */}
                <div>
                  <div className="text-sm text-gray-600 mb-1">Or select from available spots:</div>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // Get available spots - show all available spots that are not locked, filtered by zone and spot filter if selected
                    const availableSpots = yardSpots
                      .filter(spot => {
                        const hasValidDisplayNumber = spot.displayNumber && spot.displayNumber.trim();
                        const isAvailable = spot.status === 'available' && !mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id);
                        const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                        const matchesSpotFilter = !spotFilter.trim() || (hasValidDisplayNumber && spot.displayNumber.toLowerCase().includes(spotFilter.toLowerCase()));
                        return hasValidDisplayNumber && isAvailable && matchesZone && matchesSpotFilter;
                      });
                      
                    if (availableSpots.length === 0) {
                      return (
                        <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                          {spotFilter.trim() && selectedZoneFilter 
                            ? `No available yard spots found matching "${spotFilter}" in the selected zone. Try clearing the filters.`
                            : spotFilter.trim()
                            ? `No available yard spots found matching "${spotFilter}". Try clearing the filter.`
                            : selectedZoneFilter 
                            ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                            : `No available yard spots found.`
                          }
                        </div>
                      );
                    }
                    
                    return availableSpots.map(spot => {
                      // Find the zone name for this spot
                      const zone = zones.find(z => z.id === spot.zoneId);
                      
                      return (
                        <button
                          key={spot.id}
                          className={`px-2 py-1 border rounded text-sm ${
                            selectedYardSpotUuid === spot.id 
                              ? 'bg-blue-100 border-blue-500' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => {
                            onYardSpotForMoveChange(spot.displayNumber);
                            onYardSpotUuidChange(spot.id);
                            onSpotFilterChange(spot.displayNumber);
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{spot.displayNumber}</span>
                            {zone && (
                              <span className="text-xs text-gray-500">{zone.name}</span>
                            )}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
            </div>
          )}

          {selectedDestinationType === 'facility_zone' && (
            /* Facility zone selection */
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Select Facility Zone</h2>
              
              <div className="space-y-3">
                {/* Quick selection from available facility zones */}
                <div>
                  <div className="text-sm text-gray-600 mb-1">Select from available facility zones:</div>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // Get available facility zones
                      const availableFacilityZones = facilityZones || [];
                      
                      if (availableFacilityZones.length === 0) {
                        return (
                          <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                            No facility zones found.
                          </div>
                        );
                      }
                      
                      return availableFacilityZones.map((zone: any) => {
                        return (
                          <button
                            key={zone.facility_zone_uuid}
                            className={`px-3 py-2 border rounded text-sm ${
                              selectedFacilityZone === zone.facility_zone_uuid 
                                ? 'bg-blue-100 border-blue-500' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => onFacilityZoneChange(zone.facility_zone_uuid)}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{zone.facility_zone_name}</span>
                              {zone.zone_type && (
                                <span className="text-xs text-gray-500">{zone.zone_type}</span>
                              )}
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-between pt-3 border-t">
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={onAssign}
              disabled={
                (selectedDestinationType === 'yard_spot' && !selectedYardSpotForMove.trim()) ||
                (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) ||
                assigningYardSpot || validatingShipment
              }
            >
              {validatingShipment ? (
                <>
                  <CircularProgress size={24} className="mr-2" />
                  Validating...
                </>
              ) : assigningYardSpot ? (
                <CircularProgress size={24} />
              ) : (
                'Move Trailer'
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MoveToYardSpotModal;

